import { apiClient } from "api/APIClient";
import {
  Edge_Base_Backend,
  EdgeBaseFlow,
  Node_Base_Backend,
  NodeBaseFlow,
} from "api/GenericPathwayInterfaces";

/**
 * Transform frontend FlowEdges → backend schema
 */
export function transformEdgesForBackend(edges: EdgeBaseFlow[]) {
  return edges.map((edge) => {
    const sourceId = Number(edge.source);
    const targetId = Number(edge.target);

    const rawCat =
      (edge.data as any)?.edge_category ??
      (edge.data as any)?.category_id ??
      null;

    const edgeCategory =
      typeof rawCat === "number"
        ? rawCat
        : typeof rawCat === "string" && rawCat.trim() !== ""
        ? Number(rawCat)
        : undefined;

    return {
      source: Number.isFinite(sourceId) ? sourceId : edge.source,
      source_handle: edge.sourceHandle ?? null,
      target: Number.isFinite(targetId) ? targetId : edge.target,
      target_handle: edge.targetHandle ?? null,
      animated: (edge.data as any)?.animated ?? edge.animated ?? false,
      edge_category: Number.isFinite(edgeCategory as any) ? edgeCategory : undefined,
    };
  });
}


/**
 * Creates generic CRUD for Nodes
 */
export function nodeAPI<TNode extends Node_Base_Backend>(
  module: string
) {
  const base = `/${module}/nodes`;

  return {
    getAll: async (): Promise<TNode[]> => {
      const { data } = await apiClient.get<TNode[]>(`${base}/all/`);
      return data;
    },

    create: async (payload: Partial<TNode>): Promise<TNode> => {
      const { data } = await apiClient.post<TNode>(`${base}/all/`, payload);
      return data;
    },

    update: async (id: number, updates: Partial<TNode>): Promise<TNode> => {
      const payload = { ...updates } as any;

      if ((updates as any).position) {
        payload.position_x = (updates as any).position.x;
        payload.position_y = (updates as any).position.y;
        delete payload.position;
      }

      const { data } = await apiClient.patch<TNode>(`${base}/${id}/`, payload);
      return data;
    },
    
    updateField: async (id: number, field: string, value: any): Promise<TNode> => {
      const { data } = await apiClient.patch<TNode>(`${base}/${id}/`, { [field]: value });
      return data;
    },

    delete: async (id: string | number): Promise<void> => {
      await apiClient.delete(`${base}/${id}/`);
    },
  };
}

/**
 * Creates generic CRUD for Edges
 */
export function edgeAPI<TEdge extends Edge_Base_Backend>(
  module: string
) {
  const base = `/${module}/edges`;

  return {
    getAll: async (): Promise<TEdge[]> => {
      const { data } = await apiClient.get<TEdge[]>(`${base}/all/`);
      return data;
    },

    create: async (edge: EdgeBaseFlow): Promise<TEdge> => {
      const payload = transformEdgesForBackend([edge])[0];
      const { data } = await apiClient.post<TEdge>(`${base}/all/`, payload);
      return data;
    },

    update: async (edge: EdgeBaseFlow): Promise<void> => {
      const payload = transformEdgesForBackend([edge])[0];

      if (edge.id) {
        await apiClient.patch(`${base}/${edge.id}/`, payload);
      } else {
        await apiClient.post(`${base}/all/`, payload);
      }
    },

    updateField: async (id: number, field: keyof TEdge, value: any): Promise<TEdge> => {
      const { data } = await apiClient.patch<TEdge>(`${base}/${id}/`, { [field]: value });
      return data;
    },

    delete: async (id: string | number): Promise<void> => {
      await apiClient.delete(`${base}/${id}/`);
    },
  };
}
